import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Fullscreen } from "components/shared/Fullscreen/Fullscreen";
import { Button } from "@bootstrap-styled/v4";
import { Button as RBButton } from "react-bootstrap";
import { useActivity } from "hooks/useActivity/useActivity";
import { useMembers } from "hooks/livekit/useMembers/useMembers";
import { useSessionActivity } from "hooks/useSessionActivity/useSessionActivity";
import { useSessionContext } from "hooks/useSession";
import { useDataTrack } from "hooks/livekit/useDataTrack/useDataTrack";
import { VideosBar } from "components/pages/Session/shared/VideosBar/VideosBar";
import { request } from "hooks/useOkoApi/useOkoApi";
import useLocalVideoToggle from "hooks/livekit/useLocalVideoToggle/useLocalVideoToggle";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { useAuthUserDataContext } from "hooks/useAuthUserData/useAuthUserData";
import { useLocalParticipantUserId } from "hooks/useLocalParticipant/useLocalParticipant";
import { useReadyRoomHeartbeat } from "hooks/useReadyRoomHeartbeat/useReadyRoomHeartbeat";
import { SpeechBubble } from "components/shared/SpeechBubble/SpeechBubble";
import { ScreenTitle } from "components/shared/ScreenTitle/ScreenTitle";
import { VolumeControl } from "components/shared/VolumeControl/VolumeControl";
import { texts } from "config/texts";
import OKOCoreLogo from "assets/OKO_Core_Logotype2.svg";
import OKOCharacter from "assets/OKO_op2_yellow.svg";
import OKOCharacterNoMouth from "assets/OKO_op2_yellow2.svg";
import OkoAniMouth from "assets/OKO_AniMouth_HigherRez.gif";
import OKOCharacterShadow from "assets/Shadow.svg";
import { TimerIcon } from "components/shared/Icon/Timer";
import { AVATAR_FACES } from "./AvatarFaces";
import { colors, constants, logSession } from "utils";
import { checkSessionStatus, getNetworkInfo, voteToStart, redirectToGroupCode, REDIRECT_ERROR } from "components/pages/Session/sessionPrefetch";

const AVATAR_COLORS = ["#605DEC", "#BC165D", "#00A35A", "#F7622B", "#FE1E7E"];

// Random offset so the color sequence starts at a different position each session.
// Computed once at module load — stable for the lifetime of the page.
const COLOR_OFFSET = Math.floor(Math.random() * AVATAR_COLORS.length);

export const ReadyRoom = () => {
  const { session, groupCode, userRole, authMethod, activityIdHistoryRef, waitingRoomInit } = useSessionContext();
  const { authUserData } = useAuthUserDataContext();
  const okoUserId = useLocalParticipantUserId();
  const { change: changeActivity } = useSessionActivity();
  const { update: updateActivity } = useActivity();
  const { members } = useMembers({ includeObservers: false });
  const [hasVoted, setHasVoted] = useState(false);
  const votingRef = useRef(false);
  const [isVideoToggled, setIsVideoToggled] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const { toggleVideoEnabled } = useLocalVideoToggle();
  const { isVideoMuted } = useVideoContext();

  // OKO bot speech state
  const [phrase, setPhrase] = useState("");
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [showText, setShowText] = useState(false);
  const [showVolumeControl, setShowVolumeControl] = useState(false);

  // Timing refs for speech pattern logging
  const roomLandingTimeRef = useRef(Date.now());
  const lastSpeechEndRef = useRef(null);
  const speechPatternIndexRef = useRef(0);
  const [extendCount, setExtendCount] = useState(waitingRoomInit?.extend_count ?? 0);
  const [maxExtensions, setMaxExtensions] = useState(waitingRoomInit?.max_extensions ?? 2);
  const [isExtending, setIsExtending] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const countdownTotalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const dataTrackReceivedRef = useRef(false);
  const speechEndTimerRef = useRef(null);
  const countdownGraceRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(speechEndTimerRef.current);
      clearInterval(countdownIntervalRef.current);
      clearTimeout(countdownGraceRef.current);
    };
  }, []);

  // Safety net: if countdown reaches 0 and server never sends waiting_room_timeout,
  // auto-advance to waiting-room after a grace period instead of leaving student stuck.
  useEffect(() => {
    if (countdown !== 0) return;
    countdownGraceRef.current = setTimeout(() => {
      reportExit("timeout");
      changeActivity({ id: constants.ACTIVITY_ID_WAITING_ROOM });
      updateActivity(
        {
          title: texts.waitingRoomDefaultTitle,
          showParticipants: false,
          showParticipantVideos: false,
        },
        constants.ACTIVITY_ID_WAITING_ROOM
      );
    }, 5000);
    return () => clearTimeout(countdownGraceRef.current);
  }, [countdown]);

  // Randomized starting color per session, non-repeating within each round of 5.
  // Faces alternate independently.
  const getParticipantStyle = (index) => ({
    color: AVATAR_COLORS[(index + COLOR_OFFSET) % AVATAR_COLORS.length],
    faceIndex: index % AVATAR_FACES.length,
  });

  // Heartbeat logging for ready room wait time tracking
  const { totalWaitTime, resetWaitTime, reportExit } = useReadyRoomHeartbeat({
    roomName: "Ready room (Let's Begin)",
    sessionId: session?.id,
    groupCode,
    userId: authUserData?.id,
    hasClickedBegin: hasVoted,
    participantCount: members?.length || 0,
    role: userRole,
    authMethod,
  });

  // Check session status on mount — redirect if session is closed
  useEffect(() => {
    if (!session?.id) return;
    checkSessionStatus({
      sessionId: session.id,
      groupCode,
      userId: authUserData?.id,
      okoUserId,
      source: "ready_room_mount",
    }).then(({ isActive, redirectError }) => {
      if (!isActive) {
        reportExit("redirect");
        redirectToGroupCode(groupCode, redirectError, {
          source: "ready_room_mount",
          sessionId: session?.id,
        });
      }
    });
  }, [session?.id, groupCode, authUserData?.id]);

  // Initialize countdown from prefetched waiting_room data or fallback to reset_timeout API
  // Skipped if a LiveKit data track event already provided fresher state
  useEffect(() => {
    if (!session?.id || dataTrackReceivedRef.current) return;

    const initFromWaitingRoom = (data) => {
      if (dataTrackReceivedRef.current) return;
      if (data.extend_count != null) setExtendCount(data.extend_count);
      if (data.max_extensions != null) setMaxExtensions(data.max_extensions);
      if (data.seconds_remaining > 0 && data.participant_countdown_active) {
        countdownTotalRef.current = data.seconds_remaining;
        setCountdown(data.seconds_remaining);
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    };

    if (waitingRoomInit) {
      initFromWaitingRoom(waitingRoomInit);
    } else {
      // Fallback: call reset_timeout API if no prefetched data
      request(`session/${session.id}/waiting_room/reset_timeout`)
        .then((res) => {
          initFromWaitingRoom({
            extend_count: res?.extend_count,
            seconds_remaining: res?.seconds_remaining,
            participant_countdown_active: !!res?.seconds_remaining,
          });
        })
        .catch(() => {});
    }
  }, [session?.id, waitingRoomInit]);

  useEffect(() => {
    if (!isVideoToggled && !isVideoMuted) {
      toggleVideoEnabled();
      setIsVideoToggled(true);
    }
  }, []);

  useDataTrack((e) => {
    if (!["observation", "speech_start", "speech_end"].includes(e?.event_type)) {
      console.log("[ReadyRoom] LiveKit event:", e);
    }
    if (e?.event_type === "waiting_room_timeout") {
      reportExit("redirect");
      redirectToGroupCode(groupCode, REDIRECT_ERROR.SESSION_NOT_FOUND, {
        source: "waiting_room_timeout",
        sessionId: session?.id,
      });
      return;
    }
    if (e?.event_type === "speech_start") {
      const message = e?.data?.message;
      if (!message) return;
      const now = Date.now();
      const patternIndex = speechPatternIndexRef.current++;
      const sinceRoomLanding = ((now - roomLandingTimeRef.current) / 1000).toFixed(1);
      const silenceGap = lastSpeechEndRef.current
        ? ((now - lastSpeechEndRef.current) / 1000).toFixed(1)
        : null;
      console.log(`[ReadyRoom] Speech pattern #${patternIndex}`, {
        message: stripTags(message),
        timeSinceRoomLanding: `${sinceRoomLanding}s`,
        silenceSincePrevEnd: silenceGap ? `${silenceGap}s` : "first",
      });
      clearTimeout(speechEndTimerRef.current);
      setIsPlayingAudio(true);
      setShowText(true);
      setPhrase(stripTags(message));
    }
    if (e?.event_type === "speech_end") {
      lastSpeechEndRef.current = Date.now();
      console.log(`[ReadyRoom] Speech ended`);
      setIsPlayingAudio(false);
      clearTimeout(speechEndTimerRef.current);
      speechEndTimerRef.current = setTimeout(() => setShowText(false), 8000);
    }
    if (e?.event_type === "waiting_room_state_change") {
      dataTrackReceivedRef.current = true;
      const { action, seconds_remaining, extend_count, max_extensions } = e.data ?? {};
      if (extend_count != null) setExtendCount(extend_count);
      if (max_extensions != null) setMaxExtensions(max_extensions);

      const startInterval = () => {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      };

      if (action === "countdown_started") {
        countdownTotalRef.current = seconds_remaining;
        setCountdown(seconds_remaining);
        startInterval();
      }

      if (action === "extend") {
        countdownTotalRef.current = seconds_remaining;
        setCountdown(seconds_remaining);
        startInterval();
      }
    }
  });

  const onStart = async () => {
    if (votingRef.current) return;
    votingRef.current = true;
    setHasVoted(true);
    setNetworkError(false);

    // Log when user clicks "Let's Begin"
    logSession("Let's Begin clicked", {
      sessionId: session?.id,
      groupCode,
      userId: authUserData?.id,
      okoUserId,
      totalWaitTime,
      participantCount: members?.length || 0,
      role: userRole,
      authMethod,
      ...getNetworkInfo(),
    });

    // Check session status before voting — redirect if closed or retries exhausted
    const { isActive, redirectError: statusRedirectError } = await checkSessionStatus({
      sessionId: session?.id,
      groupCode,
      userId: authUserData?.id,
      okoUserId,
      source: "ready_room_vote",
    });
    if (!isActive) {
      reportExit("redirect");
      redirectToGroupCode(groupCode, statusRedirectError, {
        source: "ready_room_vote_precheck",
        sessionId: session?.id,
      });
      return;
    }

    // Reset wait time after logging the final value
    resetWaitTime();

    const { result: res, shouldRedirect, redirectError } = await voteToStart({
      sessionId: session?.id,
      groupCode,
      userId: authUserData?.id,
      okoUserId,
    });

    if (shouldRedirect) {
      reportExit("redirect");
      redirectToGroupCode(groupCode, redirectError, {
        source: "ready_room_vote",
        sessionId: session?.id,
      });
      return;
    }

    if (!res) {
      // Transient network error — restore button and show feedback so user can retry
      votingRef.current = false;
      setHasVoted(false);
      setNetworkError(true);
      return;
    }

    if (res?.votes_so_far === res?.total_votes_required) {
      if (!activityIdHistoryRef.current.has(constants.ACTIVITY_ID_MULTIPLE_CHOICE)) {
        reportExit("voted");
        changeActivity({ id: constants.ACTIVITY_ID_WAITING_ROOM });
        updateActivity(
          {
            title: texts.waitingRoomDefaultTitle,
            showParticipants: false,
            showParticipantVideos: false,
          },
          constants.ACTIVITY_ID_WAITING_ROOM
        );
      }
    }
  };

  const onExtendTime = async () => {
    setIsExtending(true);
    try {
      const res = await request(`session/${session?.id}/waiting_room/extend_timeout`);
      setExtendCount(res?.extend_count ?? extendCount + 1);
    } catch {
      // 409 means max extensions reached
    } finally {
      setIsExtending(false);
    }
  };

  const canExtend = extendCount < maxExtensions && !isExtending && (countdown === null || countdown > 5);

  const okoCharacter = isPlayingAudio ? OKOCharacterNoMouth : OKOCharacter;

  const botSection = (
    <BotCharacterArea>
      <CharacterWithVolume
        onMouseEnter={() => setShowVolumeControl(true)}
        onMouseLeave={() => setShowVolumeControl(false)}
      >
        <CharacterWrapper>
          <img src={okoCharacter} style={{ width: "210px", zIndex: 10 }} alt="OKO character" />
          <img src={OKOCharacterShadow} style={{ position: "relative", top: "-10px", width: "210px" }} alt="shadow" />
          <Mouth isPlayingAudio={isPlayingAudio} okoAniMouth={OkoAniMouth} />
        </CharacterWrapper>
        {showVolumeControl && <VolumeControl />}
      </CharacterWithVolume>
      {phrase && showText && (
        <SpeechBubble
          text={phrase}
          hide={!showText}
          setDisplay={() => setShowText(false)}
          positionLeft={232}
        />
      )}
    </BotCharacterArea>
  );

  const isSingleParticipant = members.length < 2;

  return (
    <StyledFullscreen>
      <ScreenTitle name="Ready room screen" />
      <AvatarBgBlob xmlns="http://www.w3.org/2000/svg" viewBox="0 0 580 538" fill="none">
        <path fillRule="evenodd" clipRule="evenodd" d="M362.728 -84.6724C443.397 -59.7166 440.804 55.9981 498.405 117.741C551.637 174.801 655.074 178.87 678.139 253.418C705.152 340.726 690.823 452.934 619.973 510.663C550.868 566.971 451.533 518.16 362.728 510.42C286.831 503.805 208.833 513.545 146.73 469.416C73.1347 417.122 8.87678 343.36 1.04087 253.418C-7.30901 157.577 35.221 61.8869 105.502 -3.8086C173.76 -67.6133 273.467 -112.287 362.728 -84.6724Z" fill="#FCFBF8" />
      </AvatarBgBlob>
      <div style={{ left: "24px", position: "absolute", top: "24px" }}>
        <img src={OKOCoreLogo} alt="OKO logo" />
      </div>
      <SingleParticipantWrapper>
        <BotSection>
          {botSection}
          {isSingleParticipant ? (
            <>
              <div role="status" aria-live="polite" aria-atomic="true">
                <WaitingTitle>You're in with group <GroupCode>{groupCode}</GroupCode></WaitingTitle>
                <WaitingSubtext>OKO needs groups of 2-5 students before we start.</WaitingSubtext>
              </div>
              <BeginButton disabled style={{ marginTop: "16px" }}>Waiting for your team...</BeginButton>
            </>
          ) : (
            <>
              <div role="status" aria-live="polite" aria-atomic="true">
                <WaitingTitle>Team ready? Click "Let's Go!"</WaitingTitle>
                <WaitingSubtext>Click "Add More Time" if your team needs an extra minute to get ready.</WaitingSubtext>
              </div>
              <ButtonRow>
                {!hasVoted && (
                  <BeginButton onClick={onStart}>
                    Let's Go!
                    {countdown !== null && countdown > 0 && (
                      <CountdownRing seconds={countdown} total={countdownTotalRef.current} />
                    )}
                  </BeginButton>
                )}
                <AddTimeButton onClick={onExtendTime} disabled={!canExtend}>
                  <TimerIcon clockColor={colors.pink500} color={colors.milkGlass500} width={28} />
                  Add More Time
                </AddTimeButton>
              </ButtonRow>
            </>
          )}
        </BotSection>

        {isSingleParticipant ? (
          (() => {
            const style = getParticipantStyle(0);
            const Face = AVATAR_FACES[style.faceIndex];
            return (
              <AvatarCard bgColor={style.color}>
                <Face />
                <AvatarName>{members[0]?.participant?.name}</AvatarName>
              </AvatarCard>
            );
          })()
        ) : (
          <AvatarGrid>
            {members.map(({ participant }, index) => {
              const style = getParticipantStyle(index);
              const Face = AVATAR_FACES[style.faceIndex];
              return (
                <AvatarCard key={participant.sid} bgColor={style.color}>
                  <Face />
                  <AvatarName>{participant.name}</AvatarName>
                </AvatarCard>
              );
            })}
          </AvatarGrid>
        )}
      </SingleParticipantWrapper>
      {networkError && (
        <NetworkErrorBanner role="alert">
          Network issue — check your connection and try again.
        </NetworkErrorBanner>
      )}
    </StyledFullscreen>
  );
};

const RING_SIZE = 36;
const RING_STROKE = 3;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const CountdownRing = ({ seconds, total }) => {
  const progress = total > 0 ? seconds / total : 0;
  const offset = RING_CIRCUMFERENCE * (1 - progress);

  return (
    <CountdownWrapper>
      <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth={RING_STROKE}
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="white"
          strokeWidth={RING_STROKE}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          style={{ transition: "stroke-dashoffset 1s linear" }}
        />
      </svg>
      <CountdownText>{seconds}</CountdownText>
    </CountdownWrapper>
  );
};

const CountdownWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${RING_SIZE}px;
  height: ${RING_SIZE}px;
  margin-left: auto;
`;

const CountdownText = styled.span`
  position: absolute;
  font-size: 12px;
  font-weight: 600;
  color: white;
  line-height: 1;
`;

const StyledFullscreen = styled(Fullscreen)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${colors.milkGlass500};
`;

const BeginButton = styled(Button)`
  display: flex !important;
  padding: 6px 8px 6px 24px !important;
  justify-content: center !important;
  align-items: center !important;
  gap: 8px !important;
  align-self: stretch !important;
  background: ${colors.pink700} !important;
  border: none !important;
  border-radius: 20px !important;
  font-weight: 500 !important;
  line-height: 24px !important;
  color: white !important;
  width: 100%;
  height: 48px;

  &:hover {
    background: ${colors.pink800} !important;
  }

  &:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
    pointer-events: none;
  }
`;

const NetworkErrorBanner = styled.div`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: ${colors.nightlyWoods500};
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 10;
`;

const GroupCodeLabel = styled.p`
  font-size: 14px;
  color: ${colors.milkGlass950};
  margin: 10px 0 0 0;
`;

function stripTags(str) {
  if (str === null || str === "") return "";
  return str.toString().replace(/<[^>]*>/g, "");
}

const SingleParticipantWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1024px;
  width: 100%;
  gap: 3rem;
  /* Above the decorative background blob (an absolutely positioned z-0 svg),
     which otherwise paints over the in-flow text it overlaps. */
  position: relative;
  z-index: 1;

  /* Below ~1168px the centered 1024px row reaches the right edge, where the
     control rail lives — inset the content so the avatar cards clear the rail
     (24 gutter + 48 rail + 24 margin) instead of sliding underneath it. */
  @media (max-width: 1168px) {
    box-sizing: border-box;
    padding: 0 96px 0 24px;
  }
`;

const BotSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  max-width: 344px;
`;

const BotCharacterArea = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const CharacterWithVolume = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 60px;
  margin-left: -60px;
`;

const CharacterWrapper = styled.div`
  position: relative;
  align-items: center;
  display: grid;
  justify-content: center;
  width: 210px;
  height: 210px;
`;

const Mouth = styled.div`
  background-image: url(${({ isPlayingAudio, okoAniMouth }) => isPlayingAudio ? okoAniMouth : "none"});
  width: 105px;
  height: 105px;
  left: 59px;
  top: 57px;
  border-radius: 100px;
  position: absolute;
  background-size: cover;
  z-index: 10;
`;

const WaitingTitle = styled.p`
  color: #000;
  font-family: "ABC Marfa", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: 0;
  margin: 0;
`;

const WaitingSubtext = styled.p`
  color: #000;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  margin: 0;
`;

const GroupCode = styled.span`
  color: ${colors.blue500};
  font-weight: 700;
  text-transform: uppercase;
`;

const AvatarGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
  margin-top: 16px;
`;

const AddTimeButton = styled(RBButton)`
  display: flex !important;
  padding: 16px 24px !important;
  justify-content: center !important;
  align-items: center !important;
  gap: 8px !important;
  align-self: stretch !important;
  background: transparent !important;
  border: 1px solid ${colors.pink500} !important;
  border-radius: 20px !important;
  font-weight: 500 !important;
  line-height: 24px !important;
  color: ${colors.pink500} !important;
  height: 48px;
`;

const AvatarBgBlob = styled.svg`
  position: absolute;
  top: 0;
  right: 0;
  width: 690.89px;
  height: 630.429px;
  pointer-events: none;
  z-index: 0;
`;

const AvatarCard = styled.div`
  border-radius: 11.733px;
  background: ${({ bgColor }) => bgColor};
  width: 260px;

  /* With the rail inset, a full 1024px viewport is ~20px short of fitting two
     260px cards beside the bot column — narrow the cards slightly. */
  @media (max-width: 1024px) {
    width: 240px;
  }
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  z-index: 1;
`;

const AvatarName = styled.span`
  position: absolute;
  bottom: 12px;
  left: 12px;
  max-width: calc(100% - 24px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #FAF7F2;
  background: rgba(0, 28, 15, 0.72);
  padding: 2px 8px;
  border-radius: 6px;
  font-family: Roboto, sans-serif;
  font-size: 17.6px;
  font-weight: 400;
  line-height: normal;
`;

